import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const iconOptions = [
    { value: 'DocumentCheckIcon', label: 'Document Check' },
    { value: 'ClockIcon', label: 'Clock' },
    { value: 'ShieldCheckIcon', label: 'Shield Check' },
    { value: 'MapPinIcon', label: 'Map Pin' },
    { value: 'CheckCircleIcon', label: 'Check Circle' },
    { value: 'StarIcon', label: 'Star' },
    { value: 'HeartIcon', label: 'Heart' },
    { value: 'LightBulbIcon', label: 'Light Bulb' },
    { value: 'RocketLaunchIcon', label: 'Rocket' }
];

const colorOptions = [
    { value: 'green', bgValue: 'bg-green-50', label: 'Green' },
    { value: 'blue', bgValue: 'bg-blue-50', label: 'Blue' },
    { value: 'purple', bgValue: 'bg-purple-50', label: 'Purple' },
    { value: 'orange', bgValue: 'bg-orange-50', label: 'Orange' },
    { value: 'red', bgValue: 'bg-red-50', label: 'Red' },
    { value: 'yellow', bgValue: 'bg-yellow-50', label: 'Yellow' },
    { value: 'indigo', bgValue: 'bg-indigo-50', label: 'Indigo' }
];

const FeatureModal = ({ feature, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        icon_name: 'DocumentCheckIcon',
        title: '',
        description: '',
        color_class: 'green',
        bg_color_class: 'bg-green-50',
        display_order: 0,
        is_active: true
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (feature) {
            setFormData({
                icon_name: feature.icon_name || 'DocumentCheckIcon',
                title: feature.title || '',
                description: feature.description || '',
                color_class: feature.color_class || 'green',
                bg_color_class: feature.bg_color_class || 'bg-green-50',
                display_order: feature.display_order || 0,
                is_active: feature.is_active !== undefined ? feature.is_active : true
            });
        }
    }, [feature]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'color_class') {
            const selectedColor = colorOptions.find(c => c.value === value);
            setFormData(prev => ({
                ...prev,
                color_class: value,
                bg_color_class: selectedColor?.bgValue || 'bg-green-50'
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (feature) {
                await axios.put(`/api/admin/whychoose/features/${feature.id}`, formData);
                toast.success('Feature updated successfully');
            } else {
                await axios.post('/api/admin/whychoose/features', formData);
                toast.success('Feature created successfully');
            }
            onSave();
        } catch (error) {
            console.error('Error saving feature:', error);
            toast.error('Failed to save feature');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-100">
                    <h2 className="text-xl font-bold text-slate-800">
                        {feature ? 'Edit Feature' : 'Add New Feature'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <XMarkIcon className="h-5 w-5 text-slate-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    {/* Icon Selection */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Icon
                        </label>
                        <select
                            name="icon_name"
                            value={formData.icon_name}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                        >
                            {iconOptions.map(icon => (
                                <option key={icon.value} value={icon.value}>
                                    {icon.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Title */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Title *
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                            placeholder="e.g., Low Documentation"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Description *
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            required
                            rows={3}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all resize-none"
                            placeholder="Brief description of this feature..."
                        />
                    </div>

                    {/* Color Selection */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Color Theme
                        </label>
                        <select
                            name="color_class"
                            value={formData.color_class}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                        >
                            {colorOptions.map(color => (
                                <option key={color.value} value={color.value}>
                                    {color.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Display Order */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Display Order
                        </label>
                        <input
                            type="number"
                            name="display_order"
                            value={formData.display_order}
                            onChange={handleChange}
                            min="0"
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                        />
                    </div>

                    {/* Active Toggle */}
                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            name="is_active"
                            checked={formData.is_active}
                            onChange={handleChange}
                            className="w-5 h-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                        />
                        <label className="text-sm font-medium text-slate-700">
                            Active (visible on website)
                        </label>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-6 py-3 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50"
                        >
                            {loading ? 'Saving...' : (feature ? 'Update' : 'Create')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default FeatureModal;
